import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
	IoMdArrowDroprightCircle,
	IoMdArrowDropleftCircle
} from 'react-icons/io';

import * as S from './styles';
import { formatClick, shrinkText } from 'utils/functions';
import { CarouselImagesWithDescription } from 'types/CarouselImagesWithDescription';
import { UseTranslationProps } from 'types/UseTranslationProps';

interface CarouselProps {
	items: CarouselImagesWithDescription[] | [];
	interval?: number;
	isBtns?: boolean; // se mostra os btns de passar e voltar banner
	isAutoPlay?: boolean; // se vai passar o banner sozinho
	isShowQtdBanners?: boolean; //se mostra ou não a quantidade de banners com as bolas de posição
	showClicks?: boolean; //se mostra ou não os clicks
	positionContainerBalls?: 0 | 1; //0-abaixo do carrossel, 1- dentro do carrossel
	objectFitImg?: 'cover' | 'fill' | 'contain';
}

export default function Carousel({
	items,
	interval = 3000,
	isBtns = true,
	isAutoPlay = true,
	isShowQtdBanners = true,
	showClicks = true,
	positionContainerBalls = 0,
	objectFitImg = 'cover'
}: CarouselProps) {
	const { t }: UseTranslationProps = useTranslation();
	const carrosel: any = useRef(null);
	const [currentItemIndex, setCurrentItemIndex] = useState(0);
	const currentIndexRef = useRef(currentItemIndex);

	useEffect(() => {
		currentIndexRef.current = currentItemIndex;
	}, [currentItemIndex]);

	function handlePreviousClick() {
		if (currentItemIndex > 0) {
			setCurrentItemIndex(currentItemIndex - 1);
			carrosel.current.scrollLeft -= carrosel.current.offsetWidth;
		}
	}

	const handleNextClick = () => {
		setCurrentItemIndex((prevIndex) => {
			const nextIndex = prevIndex < items.length - 1 ? prevIndex + 1 : 0;
			carrosel.current.scrollLeft = carrosel.current.offsetWidth * nextIndex;
			return nextIndex;
		});
	};

	useEffect(() => {
		if (isAutoPlay) {
			const intervalId = setInterval(() => {
				handleNextClick();
			}, interval);

			return () => clearInterval(intervalId);
		}
	}, [isAutoPlay, interval, items.length]);

	return (
		<S.container>
			{items.length > 0 ? (
				<>
					<S.CarouselContainer ref={carrosel} data-testid="containerImages">
						{items.map((item) => (
							<S.CarouselItem key={item.id}>
								<S.imageTag $objectFitImg={objectFitImg}>
									<img src={item.imageUrl} alt={item.imageUrl} />
									{showClicks && (
										<S.clicks>
											<p>{`${t('cliques')}: ${formatClick(
												item?.clicks || 0
											)}`}</p>
										</S.clicks>
									)}
								</S.imageTag>

								<S.infos>
									<S.title>{item.title}</S.title>
									<S.subtitle>{item.subtitle}</S.subtitle>
									<S.description>
										{shrinkText(item.description, 200)}
									</S.description>
								</S.infos>
							</S.CarouselItem>
						))}
					</S.CarouselContainer>

					{isBtns && (
						<>
							<S.ButtonLeft data-testid="backBtn" onClick={handlePreviousClick}>
								<IoMdArrowDropleftCircle />
							</S.ButtonLeft>
							<S.ButtonRight data-testid="nextBtn" onClick={handleNextClick}>
								<IoMdArrowDroprightCircle />
							</S.ButtonRight>
						</>
					)}

					{isShowQtdBanners &&
						(positionContainerBalls === 0 ? (
							<S.containerBallsOutsideCarousel data-testid="balls0">
								{items.map((_, i) => (
									<S.ball key={i} $selected={i === currentItemIndex} />
								))}
							</S.containerBallsOutsideCarousel>
						) : (
							<S.containerBallsInsideCarousel data-testid="balls1">
								{items.map((_, i) => (
									<S.ball key={i} $selected={i === currentItemIndex} />
								))}
							</S.containerBallsInsideCarousel>
						))}
				</>
			) : (
				<S.centralizeNotice>
					<p>{t('bannersCarousel.thereIsNotBanners')}</p>
				</S.centralizeNotice>
			)}
		</S.container>
	);
}
